// The app shell — a thin React frame over the framework-agnostic `api` client. It owns
// the chrome (header + nav + the owner identity) and, under ADR-0060, an auth state
// machine derived from `GET /api/me`:
//
//   loading → (fetching /api/me)
//   anon    → 401: no Session. Show the public Landing (except on /claim, so a newcomer
//             with a code can proceed).
//   pending → { member: false }: a signed-in Google subject with no account yet. Show the
//             join wall ("Claim your camera to get started"); /claim lets them approve
//             (signup = a first Claim).
//   member  → { member: true }: the full app (gallery/devices/looks/claim) + the account
//             header (email + Sign out).
//
// A 401 that lands AFTER we're a member (the Session lapsed mid-use) is distinct: the
// client wrapper flips the shell to the SignedOut re-login screen, not the Landing.
// Cloudflare Access is gone — sign-in is `/auth/login`, sign-out is `POST /auth/logout`.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type ApiClient, ApiError, createApiClient } from "./api";
import { useRoute, isActivePath } from "./router";
import { Gallery } from "./components/Gallery";
import { ClaimScreen } from "./components/ClaimScreen";
import { DeviceManager } from "./components/DeviceManager";
import { LooksPage } from "./components/LooksPage";
import { GuestsPage } from "./components/GuestsPage";
import { AlbumsPage } from "./components/AlbumsPage";
import { AlbumDetail } from "./components/AlbumDetail";
import { PublicAlbum } from "./components/PublicAlbum";
import { Landing } from "./components/Landing";
import { startLogin } from "./auth";
import { SignedOut } from "./components/SignedOut";
import { Loading } from "./components/Loading";
import { ThemeSwitcher } from "./components/ThemeSwitcher";
import type { MediaItem } from "./api";
import { isClip } from "./cores";
import type { SheetCores } from "./cores";
import type { BuildMosaicOptions, MosaicSelectionItem } from "./cores";

export interface AppProps {
  /** Injectable for tests; defaults to the real same-origin client. */
  client?: ApiClient;
  /** Injectable download flow (tests stub it); falls through to the real one otherwise. */
  onDownload?: (item: MediaItem) => Promise<void>;
  /** Injectable contact-Sheet decode/render seam (tests stub it); defaults to the real cores. */
  sheetCores?: SheetCores;
  /** Injectable Mosaic builder (tests stub it — WebCodecs is browser-only). */
  buildMosaic?: (
    items: MosaicSelectionItem[],
    options: BuildMosaicOptions,
  ) => Promise<Blob>;
}

/** The shell's derived auth state, from `GET /api/me` (ADR-0060). */
type AuthState = "loading" | "anon" | "pending" | "member";

/**
 * Wrap the client so an `/api/*` 401 that fires while we're already a member (the Session
 * lapsed mid-use) can flip the shell to the re-login screen — the per-screen hooks don't
 * re-check `/api/me`, so this is how a stale session surfaces. `onUnauthorized` decides
 * whether to act (it no-ops unless we're a member), so the initial-load 401 → Landing case
 * is untouched. A transparent pass-through otherwise: it only adds a rethrowing `.catch`.
 */
function watchUnauthorized(client: ApiClient, onUnauthorized: () => void): ApiClient {
  return new Proxy(client, {
    get(target, prop) {
      const value = Reflect.get(target, prop) as unknown;
      if (typeof value !== "function") return value;
      return (...args: unknown[]) => {
        const out = (value as (...a: unknown[]) => unknown).apply(target, args);
        return out instanceof Promise
          ? out.catch((err: unknown) => {
              // Only a 401 (no/expired Session) means "re-login". A 403 is an
              // authorization refusal (the join wall, or a future per-resource deny)
              // that a re-login wouldn't fix — leave those to the per-screen states.
              if (err instanceof ApiError && err.status === 401) onUnauthorized();
              throw err;
            })
          : out;
      };
    },
  });
}

export function App({
  client,
  onDownload,
  sheetCores,
  buildMosaic,
}: AppProps): React.JSX.Element {
  const baseClient = useMemo(() => client ?? createApiClient(), [client]);
  const route = useRoute();

  // Public share route (/a/<token>[/<cosmetic-slug>]) — a visitor has NO login and NO Session.
  // Detected BEFORE any auth logic so the shell renders the standalone <PublicAlbum> below and
  // NEVER calls /api/me (see the guarded refreshMe effect + the early return). Only the token is
  // load-bearing; the optional slug segment is cosmetic.
  const publicToken = (() => {
    const m = /^\/a\/([^/]+)(?:\/[^/]+)?$/.exec(route.path);
    return m ? decodeURIComponent(m[1]!) : null;
  })();

  const [auth, setAuth] = useState<AuthState>("loading");
  const [email, setEmail] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  // True on a guest Session (the side door): hides the Guests panel + marks the header.
  const [guestSession, setGuestSession] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  // A 401 that arrives after we were a member: the Session lapsed while browsing.
  const [sessionExpired, setSessionExpired] = useState(false);
  // The Google sign-in bounce-back (auth.ts redirects to /?auth=cancelled|failed). Without
  // surfacing it, a failed sign-in silently re-lands on the Landing and the user loops.
  const [authNotice, setAuthNotice] = useState<"cancelled" | "failed" | null>(null);
  useEffect(() => {
    const flag = route.query.get("auth");
    if (flag === "cancelled" || flag === "failed") {
      setAuthNotice(flag);
      // Strip the param so a reload / copied URL doesn't re-announce a stale failure.
      const url = new URL(window.location.href);
      url.searchParams.delete("auth");
      window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    }
  }, [route.query]);
  // A gallery Capture/Clip sent to the Look editor as its source/palette seed (survives the route
  // change). `isClip` tells the editor to decode it as an animated GIF, not a single still.
  const [editorSeed, setEditorSeed] = useState<{ url: string; isClip: boolean } | null>(null);
  // Route-change focus management (#139): move focus to <main> and announce the new
  // section so keyboard/SR users land on the new screen, not the just-clicked link.
  const mainRef = useRef<HTMLElement>(null);
  const [announce, setAnnounce] = useState("");
  const isFirstRender = useRef(true);

  // A live mirror of `auth` for the client wrapper's closure, so it can tell an initial
  // 401 (still "loading" → Landing) from a mid-session one ("member" → SignedOut).
  const authRef = useRef<AuthState>(auth);
  authRef.current = auth;

  const api = useMemo(
    () =>
      watchUnauthorized(baseClient, () => {
        if (authRef.current === "member") setSessionExpired(true);
      }),
    [baseClient],
  );

  // Resolve (or re-resolve) the auth state from /api/me. Also called after a successful
  // Claim — signup = a first Claim, so a `pending` subject becomes a `member`.
  const refreshMe = useCallback(async (): Promise<void> => {
    try {
      const me = await api.getMe();
      if (me.member) {
        setAuth("member");
        setUserId(me.userId ?? null);
        setEmail(me.email ?? null);
        setDisplayName(me.displayName ?? null);
        setGuestSession(me.guest === true);
      } else {
        setAuth("pending");
      }
    } catch {
      // 401 → anonymous (the public Landing). A rarer non-auth failure (5xx/timeout) also
      // lands there rather than hanging — the Landing's "Log in" recovers in one click.
      setAuth("anon");
    }
  }, [api]);

  useEffect(() => {
    // A public /a/<token> visitor has no Session — never probe /api/me (it would 401 and,
    // worse, is the owner lane a public visitor must never touch).
    if (publicToken != null) return;
    void refreshMe();
  }, [refreshMe, publicToken]);

  const onClaim = route.path === "/claim";
  const onDevices = route.path === "/devices";
  const onLooks = route.path === "/looks";
  const onAlbums = route.path === "/albums";
  const onGuests = route.path === "/guests";
  // The screen dispatch stays EXACT (unlike isActivePath's prefix match), so /albums/:id is parsed
  // here: the id is the single non-slash segment after /albums/. Null unless we're on a detail route.
  const albumId = /^\/albums\/([^/]+)$/.exec(route.path)?.[1] ?? null;
  const section = onLooks
    ? "looks"
    : onDevices
      ? "cameras"
      : onAlbums || albumId
        ? "albums"
        : onGuests && !guestSession
          ? "guests"
          : onClaim
            ? "claim"
            : "gallery";
  // The member chrome (primary nav + account) shows only for a signed-in Owner in the app.
  const showAppChrome = auth === "member" && !sessionExpired;

  // On every route change: reset scroll to the top (SPA nav otherwise keeps the prior page's scroll —
  // e.g. "Use in editor" from a scrolled-down gallery opened /looks pinned to the bottom), set a
  // per-route document title ("Pixie · Looks"), and — after the first paint — move focus to <main>
  // and announce the section via an aria-live region (#139), so a nav doesn't strand focus on the
  // clicked link with the new screen unannounced. The first render is skipped so we don't yank focus
  // off the page on load.
  useEffect(() => {
    window.scrollTo(0, 0);
    const label = `${section.charAt(0).toUpperCase()}${section.slice(1)}`;
    document.title = `Pixie · ${label}`;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setAnnounce(label);
    mainRef.current?.focus();
  }, [route.path, section]);

  function nav(e: React.MouseEvent, to: string): void {
    e.preventDefault();
    route.navigate(to);
  }

  /** The "Skip to content" link target — jump focus past the header straight into <main>.
   *  focus() already scrolls the target into view, so no explicit scroll is needed. */
  function skipToMain(e: React.MouseEvent): void {
    e.preventDefault();
    mainRef.current?.focus();
  }

  /** Take a gallery Capture into the Look editor as the source/palette seed, then route to /looks.
   *  Palette/preview inputs only — never baked into the byte-pinned Look (ADR-0028). */
  function useInEditor(item: MediaItem): void {
    setEditorSeed({ url: item.url, isClip: isClip(item.key) });
    route.navigate("/looks");
  }

  /** Sign out: drop the Session server-side, then fall back to the public Landing. */
  async function signOut(): Promise<void> {
    try {
      await api.logout();
    } catch {
      // Best-effort — clear the local session state regardless of a network hiccup.
    }
    setSessionExpired(false);
    setEmail(null);
    setDisplayName(null);
    setUserId(null);
    setAuth("anon");
    route.navigate("/");
  }

  /** The main-content region for the current auth state + route. */
  function content(): React.JSX.Element {
    if (sessionExpired) return <SignedOut />;
    if (auth === "loading") return <Loading label="Loading…" />;

    // /claim is reachable in every signed state: anon (sign in to claim), pending (approve
    // → signup), member (claim another camera). ClaimScreen picks its mode from `signedIn`.
    const claimScreen = (
      <ClaimScreen
        client={api}
        initialCode={route.query.get("code") ?? undefined}
        onNavigate={route.navigate}
        signedIn={auth === "member" || auth === "pending"}
        onClaimed={() => void refreshMe()}
      />
    );

    if (auth === "anon") {
      return onClaim ? claimScreen : <Landing onClaim={() => route.navigate("/claim")} />;
    }

    if (auth === "pending") {
      if (onClaim) return claimScreen;
      // The join wall — a signed-in subject with no account yet. Claiming is the only way in.
      return (
        <div className="signed-out" data-testid="join-wall">
          <h2 className="signed-out__title">Claim your camera to get started</h2>
          <p className="signed-out__body">
            You’re signed in, but you don’t own a camera yet. Claim one to create your
            account and start syncing your pixel art.
          </p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => route.navigate("/claim")}
            data-testid="join-claim"
          >
            Claim your camera
          </button>
        </div>
      );
    }

    // member
    if (onClaim) return claimScreen;
    if (onDevices) return <DeviceManager client={api} onClaim={() => route.navigate("/claim")} />;
    if (onLooks) return <LooksPage client={api} seed={editorSeed ?? undefined} />;
    // A guest hitting /guests directly falls through to the gallery (the lane 403s it anyway).
    if (onGuests && !guestSession) return <GuestsPage client={api} />;
    if (albumId)
      return (
        <AlbumDetail
          client={api}
          id={albumId}
          onDownload={onDownload}
          onBack={() => route.navigate("/albums")}
          onDeleted={() => route.navigate("/albums")}
        />
      );
    if (onAlbums) return <AlbumsPage client={api} onOpen={(id) => route.navigate(`/albums/${id}`)} />;
    return (
      <Gallery
        client={api}
        onDownload={onDownload}
        sheetCores={sheetCores}
        buildMosaic={buildMosaic}
        onUseInEditor={useInEditor}
        onClaim={() => route.navigate("/claim")}
      />
    );
  }

  // A public share visitor gets the standalone read-only viewer as the ENTIRE app output — no
  // header, nav, or account chrome (and, per the guarded effect above, no /api/me). The theme
  // wrapper (.app-shell / .app) is kept so it still looks like Pixie. All hooks above run first,
  // so this early return doesn't violate the rules of hooks.
  if (publicToken != null) {
    return (
      <div className="app-shell">
        <div className="app">
          <main className="app__main">
            <PublicAlbum
              token={publicToken}
              onDownload={onDownload}
              buildMosaic={buildMosaic}
              sheetCores={sheetCores}
            />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main" onClick={skipToMain}>
        Skip to content
      </a>
      <header className="app__header">
        <div className="app__header-inner">
        <span className="app__brand">
          <a
            className="app__title"
            href="/"
            onClick={(e) => nav(e, "/")}
            data-testid="nav-home"
          >
            <b>PIX</b>IE
          </a>
          {showAppChrome && <span className="app__brandsub">· {section}</span>}
        </span>
        {showAppChrome && (
          <nav className="app__nav" aria-label="Primary">
            <a
              href="/"
              className={`app__navlink${isActivePath(route.path, "/") ? " app__navlink--active" : ""}`}
              onClick={(e) => nav(e, "/")}
            >
              Gallery
            </a>
            <a
              href="/devices"
              className={`app__navlink${isActivePath(route.path, "/devices") ? " app__navlink--active" : ""}`}
              onClick={(e) => nav(e, "/devices")}
              data-testid="nav-devices"
            >
              Cameras
            </a>
            <a
              href="/looks"
              className={`app__navlink${isActivePath(route.path, "/looks") ? " app__navlink--active" : ""}`}
              onClick={(e) => nav(e, "/looks")}
              data-testid="nav-looks"
            >
              Looks
            </a>
            <a
              href="/albums"
              className={`app__navlink${isActivePath(route.path, "/albums") ? " app__navlink--active" : ""}`}
              onClick={(e) => nav(e, "/albums")}
              data-testid="nav-albums"
            >
              Albums
            </a>
            {/* The Guests panel is the Owner's alone — a guest session doesn't even see the entry
               (and the API refuses it regardless, so hiding is honesty, not the protection). */}
            {!guestSession && (
              <a
                href="/guests"
                className={`app__navlink${isActivePath(route.path, "/guests") ? " app__navlink--active" : ""}`}
                onClick={(e) => nav(e, "/guests")}
                data-testid="nav-guests"
              >
                Guests
              </a>
            )}
          </nav>
        )}
        {showAppChrome && (
          <span className="app__account">
            {/* Prefer the Google display name — friendlier and shorter on mobile than the raw
                email, which stays discoverable in the tooltip. */}
            {/* A guest session is honest about itself: the badge says whose account this is NOT. */}
            {guestSession && (
              <span className="app__guestbadge" data-testid="guest-badge" title="Signed in through the guest side door">
                guest
              </span>
            )}
            <span className="app__account-email" title={email ?? userId ?? undefined}>
              {displayName ?? email ?? "signed in"}
            </span>
            {/* Real sign-out: POST /auth/logout (drops the Session), then the public Landing. */}
            <button
              type="button"
              className="app__signout"
              onClick={() => void signOut()}
              data-testid="sign-out"
            >
              Sign out
            </button>
          </span>
        )}
        {auth === "anon" && !sessionExpired && (
          <span className="app__account">
            {/* Google is quiet: "Log in" sits in the header top-right, not the Landing hero.
               Preserve pathname + query so a /claim?code=… returnTo survives the round-trip. */}
            <button
              type="button"
              className="app__signout"
              onClick={() => startLogin(window.location.pathname + window.location.search)}
              data-testid="nav-login"
            >
              Log in
            </button>
          </span>
        )}
        {auth === "pending" && !sessionExpired && (
          <span className="app__account">
            {/* Signed in with Google but no account yet — always allow switching accounts,
               so a wrong-account sign-in isn't dead-ended on the join wall. */}
            <button
              type="button"
              className="app__signout"
              onClick={() => void signOut()}
              data-testid="pending-sign-out"
            >
              Sign out
            </button>
          </span>
        )}
        <ThemeSwitcher />
        </div>
      </header>

      <div className="app">
      <div className="visually-hidden" aria-live="polite" role="status" data-testid="route-announce">
        {announce}
      </div>
      {authNotice && auth !== "member" && (
        <p className="app__auth-notice" role="alert" data-testid="auth-notice">
          {authNotice === "cancelled"
            ? "Sign-in was cancelled — nothing changed."
            : "Sign-in didn’t complete — please try again."}
          <button type="button" className="btn" onClick={() => setAuthNotice(null)}>
            Dismiss
          </button>
        </p>
      )}
      <main className="app__main" id="main" ref={mainRef} tabIndex={-1} aria-label="Main content">
        {content()}
      </main>
      </div>
    </div>
  );
}
